import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Form, Button, Container, Alert, Table, Modal, OverlayTrigger, Tooltip, Tabs, Tab, Card, Spinner } from 'react-bootstrap';
import { inventoryItemAPI } from '../api/inventoryItemAPI';
import { locationAPI } from '../api/locationAPI';
import { warehouseAPI } from '../api/warehouseAPI';
import { calculateLandedCosts } from '../api/poAPI';
import CostHistoryButton from '../components/CostHistoryButton';
import QuantityPicker from '../components/QuantityPicker';
import "bootstrap-icons/font/bootstrap-icons.css";
import Select from 'react-select';
import { formatErrorResponse } from '../utils/errorHandlers';
import { formatCurrency, formatQuantity } from '../utils/commonUtils';

function BatchReceiveScreen() {
    const navigate = useNavigate();
    const location = useLocation();
    const { confirmation, warehouse, vendor, fromVendorConfirmation, poId } = location.state || {};
    
    const [error, setError] = useState(null);
    const [locations, setLocations] = useState([]);
    const [warehouses, setWarehouses] = useState(warehouse ? [warehouse] : []);
    const [selectedWarehouse, setSelectedWarehouse] = useState(warehouse || null);
    const [items, setItems] = useState([]);
    const [isCalculating, setIsCalculating] = useState(false);
    const [showCostModal, setShowCostModal] = useState(false);
    const [calculatedCosts, setCalculatedCosts] = useState(null);
    const [markupPercentage, setMarkupPercentage] = useState(30);

    const handleCalculateCosts = async () => {
        try {
            setIsCalculating(true);
            setError(null);

            // Get unique confirmed line IDs (in case of split rows)
            const confirmedLineIds = [...new Set(items.map(item => item.confirmed_line_id))];

            // Call the enhanced API to calculate comprehensive costs with markup
            const response = await calculateLandedCosts(confirmedLineIds, markupPercentage);
            setCalculatedCosts(response);
            setShowCostModal(true);
        } catch (error) {
            setError(formatErrorResponse(error));
        } finally {
            setIsCalculating(false);
        }
    };

    const handleApplyCosts = () => {
        if (!calculatedCosts) return;

        setItems(prev => prev.map(item => {
            const costData = calculatedCosts.find(cost => cost.confirmed_line_id === item.confirmed_line_id);
            if (costData) {
                return {
                    ...item,
                    cost: parseFloat(costData.suggested_cost).toFixed(2), // Apply suggested landed cost rounded to cents
                    resale_price: parseFloat(costData.suggested_resale_price).toFixed(2), // Store suggested resale price rounded to cents
                    cost_breakdown: costData.cost_breakdown // Store detailed breakdown
                };
            }
            return item;
        }));

        setShowCostModal(false);
    };

    // Fetch warehouses on mount
    useEffect(() => {
        let isMounted = true;

        async function fetchWarehouses() {
            try {
                const response = await warehouseAPI.getAll();
                if (!isMounted) return;
                
                setWarehouses(response);
                
                // If we have an initial warehouse ID, find and set the full warehouse object
                if (warehouse && warehouse._id) {
                    const fullWarehouse = response.find(w => w._id === warehouse._id);
                    if (fullWarehouse) {
                        setSelectedWarehouse(fullWarehouse);
                    }
                }
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching warehouses:', error);
                setError('Failed to load warehouses');
            }
        }
        
        fetchWarehouses();

        return () => {
            isMounted = false;
        };
    }, [warehouse]);

    // Initialize items from confirmation
    useEffect(() => {
        if (confirmation) {
            // Transform confirmation lines into items state
            const transformedItems = confirmation.confirmed_lines
                .filter(line => !line.received_date) // Only include non-received items
                .map(line => ({
                    row_id: `row_${line._id}`,           // Unique row identifier
                    confirmed_line_id: line._id,
                    stock_item_id: line.stock_item_id,
                    stock_item: line.stock_item,
                    stock_item_case_pack: line.stock_item_case_pack || 1,
                    stock_item_uom: line.stock_item_uom || '',
                    quantity: line.confirmed_quantity,
                    original_quantity: line.confirmed_quantity, // Original PO quantity (immutable)
                    is_added_row: false,                        // True for added rows (not the original)
                    parent_row_id: null,                        // References original row's row_id
                    lot: line.lot || '',
                    bb_date: line.bb_date || '',
                    notes: '',
                    location: '',
                    po_price: line.po_line?.price || 0,
                    cost: 0, // This should be populated from PO line if available
                    warehouse_id: selectedWarehouse?._id || warehouse?._id,
                    vendor_id: vendor._id
                }));
            setItems(transformedItems);
        }
    }, [confirmation, vendor, selectedWarehouse, warehouse]);

    // Fetch locations when warehouse changes
    useEffect(() => {
        let isMounted = true;

        async function fetchLocations() {
            try {
                if (!selectedWarehouse?._id) {
                    setLocations([]);
                    return;
                }
                
                const response = await locationAPI.getAll(selectedWarehouse._id);
                if (!isMounted) return;
                
                setLocations(response);
                
                // Find and set the default floor location for all items
                const floorLocation = response.find(loc => loc.name.endsWith('-FLOOR'));
                if (floorLocation && isMounted) {
                    setItems(prev => prev.map(item => ({
                        ...item,
                        location: floorLocation._id,
                        warehouse_id: selectedWarehouse._id
                    })));
                }
            } catch (error) {
                if (!isMounted) return;
                console.error('Error fetching locations:', error);
                setError('Failed to load locations');
            }
        }
        
        fetchLocations();

        return () => {
            isMounted = false;
        };
    }, [selectedWarehouse]);

    const handleWarehouseChange = (selectedOption) => {
        const newWarehouse = warehouses.find(w => w._id === selectedOption.value);
        setSelectedWarehouse(newWarehouse);
    };

    const handleLocationChange = (itemIndex, locationId) => {
        setItems(prev => {
            const newItems = [...prev];
            newItems[itemIndex] = {
                ...newItems[itemIndex],
                location: locationId
            };
            return newItems;
        });
    };

    const handleDateChange = (itemIndex, dateString) => {
        setItems(prev => {
            const newItems = [...prev];
            newItems[itemIndex] = {
                ...newItems[itemIndex],
                bb_date: dateString
            };
            return newItems;
        });
    };

    const handleInputChange = (itemIndex, field, value) => {
        setItems(prev => {
            const newItems = [...prev];
            newItems[itemIndex] = {
                ...newItems[itemIndex],
                [field]: value
            };
            return newItems;
        });
    };

    const handleAddRow = (rowIndex) => {
        setItems(prev => {
            const sourceRow = prev[rowIndex];
            // Find the original row to get the parent_row_id
            const parentRowId = sourceRow.is_added_row ? sourceRow.parent_row_id : sourceRow.row_id;

            const newRow = {
                row_id: `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                confirmed_line_id: sourceRow.confirmed_line_id,
                stock_item_id: sourceRow.stock_item_id,
                stock_item: sourceRow.stock_item,
                stock_item_case_pack: sourceRow.stock_item_case_pack,
                stock_item_uom: sourceRow.stock_item_uom,
                quantity: 0,  // Start with 0, user adjusts manually
                original_quantity: sourceRow.original_quantity,
                is_added_row: true,
                parent_row_id: parentRowId,
                lot: '',      // Empty LOT for new row
                bb_date: '',  // Empty BB date for new row
                notes: sourceRow.notes,      // Copy notes from source
                location: sourceRow.location, // Copy location from source
                po_price: sourceRow.po_price,  // Copy PO price from source
                cost: sourceRow.cost,         // Copy cost from source
                warehouse_id: sourceRow.warehouse_id,
                vendor_id: sourceRow.vendor_id,
                resale_price: sourceRow.resale_price, // Copy resale price if set
                cost_breakdown: sourceRow.cost_breakdown // Copy cost breakdown if set
            };

            // Insert the new row right after the current row
            const newItems = [...prev];
            newItems.splice(rowIndex + 1, 0, newRow);
            return newItems;
        });
    };

    const handleRemoveRow = (rowIndex) => {
        setItems(prev => {
            const rowToRemove = prev[rowIndex];
            // Only allow removing added rows
            if (!rowToRemove.is_added_row) return prev;

            const newItems = [...prev];
            newItems.splice(rowIndex, 1);
            return newItems;
        });
    };

    // Get quantity validation status for a confirmed line
    const getQuantityValidationForLine = (confirmedLineId) => {
        const lineItems = items.filter(item => item.confirmed_line_id === confirmedLineId);
        if (lineItems.length === 0) return { isValid: true, total: 0, expected: 0 };

        const total = lineItems.reduce((sum, item) => sum + (parseFloat(item.quantity) || 0), 0);
        const expected = lineItems[0].original_quantity;
        return {
            isValid: total === expected,
            total,
            expected
        };
    };

    // Check if a row is an original row that has added rows (cannot be removed)
    const hasAddedRows = (rowId) => {
        return items.some(item => item.parent_row_id === rowId);
    };

    const isFormValid = () => {
        // Check each item has required fields
        const allItemsValid = items.every(item =>
            item.location && // Location is required
            (!isNaN(item.cost) && item.cost > 0) && // Cost must be a positive number
            item.quantity > 0 // Quantity must be positive (rows with 0 cannot be submitted)
        );

        if (!allItemsValid) return false;

        // Check quantity totals match for each confirmed line
        const confirmedLineIds = [...new Set(items.map(item => item.confirmed_line_id))];
        const allQuantitiesValid = confirmedLineIds.every(lineId => {
            const validation = getQuantityValidationForLine(lineId);
            return validation.isValid;
        });

        return allQuantitiesValid;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            await inventoryItemAPI.receiveBatch(items);
            
            // Check if user came from vendor confirmation page
            if (fromVendorConfirmation && poId) {
                // Navigate back to the PO details page (vendor confirmations)
                navigate(`/purchase-order/${poId}`, {
                    state: { 
                        update: true,
                        showVendorConfirmations: true 
                    }
                });
            } else {
                // Default behavior: go back to previous screen
                navigate(-1);
            }
        } catch (error) {
            setError(formatErrorResponse(error));
        }
    };

    if (!confirmation || !warehouse || !vendor) {
        return (
            <Container>
                <Alert variant="danger">
                    Missing required data. Please navigate to this page from a vendor confirmation.
                </Alert>
            </Container>
        );
    }

    return (
        <Container>
            <h1>Receive Vendor Shipment</h1>
            <div className="mb-4">
                <strong>Vendor:</strong> {vendor.name}<br />
                <strong>Container:</strong> {confirmation.container_number}
            </div>

            {/* Warehouse Selection */}
            <Form.Group className="mb-4">
                <Form.Label><strong>Warehouse*</strong></Form.Label>
                <Select
                    value={selectedWarehouse ? { 
                        value: selectedWarehouse._id, 
                        label: selectedWarehouse.name 
                    } : null}
                    onChange={handleWarehouseChange}
                    options={warehouses.map(w => ({
                        value: w._id,
                        label: w.name
                    }))}
                    isSearchable
                    placeholder="Select Warehouse"
                    className="react-select-container"
                    classNamePrefix="react-select"
                />
            </Form.Group>

            {/* Cost Calculation */}
            <div className="card mb-4">
                <div className="card-header">
                    <h5 className="mb-0">Cost Calculation</h5>
                </div>
                <div className="card-body">
                    <div className="row align-items-end">
                        <div className="col-md-4">
                            <Form.Group>
                                <Form.Label>
                                    <strong>Profit Margin (%)</strong>
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip>
                                                The desired profit margin for resale pricing. This percentage is applied to the final selling price, ensuring the specified profit on each sale.
                                            </Tooltip>
                                        }
                                    >
                                        <i className="bi bi-info-circle ms-1" style={{cursor: 'pointer'}}></i>
                                    </OverlayTrigger>
                                </Form.Label>
                                <Form.Control
                                    type="number"
                                    min="0"
                                    max="50"
                                    step="0.1"
                                    value={markupPercentage}
                                    onChange={(e) => setMarkupPercentage(parseFloat(e.target.value) || 30)}
                                    placeholder="30"
                                    autoComplete="off"
                                    spellCheck="false"
                                />
                                <Form.Text className="text-muted">
                                    Used for suggested resale price calculation
                                </Form.Text>
                            </Form.Group>
                        </div>
                        <div className="col-md-4">
                            <Button
                                variant="primary"
                                onClick={handleCalculateCosts}
                                disabled={isCalculating}
                                className="mb-3"
                            >
                                {isCalculating ? (
                                    <>
                                        <i className="bi bi-hourglass-split me-2"></i>
                                        Analyzing...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-robot me-2"></i>
                                        AI Cost Analysis
                                    </>
                                )}
                            </Button>
                        </div>
                    </div>
                </div>
            </div>

            <Form onSubmit={handleSubmit}>

                <div className="table-responsive">
                    <Table striped bordered hover style={{ minWidth: '1450px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}></th>
                                <th>Item</th>
                                <th>Quantity*</th>
                                <th style={{ minWidth: '200px' }}>Location*</th>
                                <th>LOT</th>
                                <th>BB Date</th>
                                <th>PO Price</th>
                                <th>Cost*</th>
                                <th>Notes</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((item, index) => {
                                const quantityValidation = getQuantityValidationForLine(item.confirmed_line_id);
                                const isFirstRowForLine = index === 0 || items[index - 1].confirmed_line_id !== item.confirmed_line_id;
                                const isLastRowForLine = index === items.length - 1 || items[index + 1]?.confirmed_line_id !== item.confirmed_line_id;
                                const hasMultipleRows = items.filter(i => i.confirmed_line_id === item.confirmed_line_id).length > 1;

                                return (
                                    <tr
                                        key={item.row_id}
                                        style={item.is_added_row ? { backgroundColor: '#f8f9fa' } : {}}
                                    >
                                        <td>
                                            <div className="d-flex gap-1">
                                                <OverlayTrigger
                                                    placement="top"
                                                    overlay={<Tooltip>Add another row for this item with different LOT/BB date</Tooltip>}
                                                >
                                                    <Button
                                                        variant="outline-primary"
                                                        size="sm"
                                                        onClick={() => handleAddRow(index)}
                                                    >
                                                        <i className="bi bi-plus-lg"></i>
                                                    </Button>
                                                </OverlayTrigger>
                                                {item.is_added_row && (
                                                    <OverlayTrigger
                                                        placement="top"
                                                        overlay={<Tooltip>Remove this row</Tooltip>}
                                                    >
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => handleRemoveRow(index)}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </Button>
                                                    </OverlayTrigger>
                                                )}
                                            </div>
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                {item.is_added_row && (
                                                    <span className="me-2 text-muted" style={{ fontSize: '0.9em' }}>└─</span>
                                                )}
                                                <span>{item.stock_item}</span>
                                            </div>
                                            {isLastRowForLine && hasMultipleRows && !quantityValidation.isValid && (
                                                <div className="text-danger small mt-1">
                                                    <i className="bi bi-exclamation-triangle me-1"></i>
                                                    Total: {formatQuantity(quantityValidation.total)} / Expected: {formatQuantity(quantityValidation.expected)}
                                                </div>
                                            )}
                                        </td>
                                        <td>
                                            <QuantityPicker
                                                casePack={item.stock_item_case_pack || 1}
                                                uom={item.stock_item_uom || ''}
                                                value={String(item.quantity)}
                                                onChange={(qty) => handleInputChange(index, 'quantity', parseFloat(qty) || 0)}
                                                required
                                            />
                                        </td>
                                        <td>
                                            <Select
                                                value={locations.find(loc => loc._id === item.location)
                                                    ? { value: item.location, label: locations.find(loc => loc._id === item.location).name }
                                                    : null}
                                                onChange={(selected) => handleLocationChange(index, selected?.value)}
                                                options={locations.map(loc => ({
                                                    value: loc._id,
                                                    label: loc.name
                                                }))}
                                                isSearchable
                                                required
                                                placeholder="Select Location"
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={item.lot}
                                                onChange={(e) => handleInputChange(index, 'lot', e.target.value.toUpperCase())}
                                                placeholder="Enter LOT"
                                                style={{ textTransform: 'uppercase' }}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="date"
                                                value={item.bb_date || ''}
                                                onChange={(e) => handleDateChange(index, e.target.value)}
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </td>
                                        <td className="text-end">
                                            ${parseFloat(item.po_price).toFixed(2)}
                                        </td>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <Form.Control
                                                    type="number"
                                                    min="0"
                                                    step="0.0001"
                                                    value={item.cost}
                                                    onChange={(e) => handleInputChange(index, 'cost', parseFloat(e.target.value))}
                                                    required
                                                    placeholder="Enter cost"
                                                    style={{ flex: 1 }}
                                                    autoComplete="off"
                                                    spellCheck="false"
                                                />
                                                <CostHistoryButton
                                                    stockItemId={item.stock_item_id}
                                                    stockItemDescription={item.stock_item}
                                                />
                                            </div>
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="text"
                                                value={item.notes}
                                                onChange={(e) => handleInputChange(index, 'notes', e.target.value)}
                                                placeholder="Enter notes"
                                                autoComplete="off"
                                                spellCheck="false"
                                            />
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                </div>

                {error && (
                    <Alert variant="danger" onClose={() => setError(null)} dismissible>
                        {error}
                    </Alert>
                )}

                <div className="d-flex gap-2 mt-3">
                    <Button 
                        variant="success" 
                        type="submit"
                        disabled={!isFormValid()}
                    >
                        Receive All Items
                    </Button>
                    <Button 
                        variant="outline-primary" 
                        onClick={() => navigate(-1)}
                    >
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to List
                    </Button>
                </div>
            </Form>

            {/* Enhanced Cost Calculation Modal */}
            <Modal show={showCostModal} onHide={() => setShowCostModal(false)} size="xl">
                <Modal.Header closeButton>
                    <Modal.Title>AI-Enhanced Landed Cost Calculator</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {calculatedCosts && calculatedCosts.length > 0 && (
                        <Tabs defaultActiveKey="summary" id="cost-analysis-tabs">
                            {/* Summary Tab */}
                            <Tab eventKey="summary" title="Cost Summary">
                                <div className="mt-3">
                                    {/* Shipment Overview */}
                                    <Card className="mb-4">
                                        <Card.Header>
                                            <h5 className="mb-0">Shipment Overview</h5>
                                        </Card.Header>
                                        <Card.Body>
                                            <div className="row">
                                                <div className="col-md-6">
                                                    <strong>Total Vendor Value:</strong> formatCurrency(calculatedCosts[0]?.shipment_breakdown?.total_vendor_value || 0)<br/>
                                                    <strong>Total Freight Cost:</strong> formatCurrency(calculatedCosts[0]?.shipment_breakdown?.total_freight_cost || 0)<br/>
                                                    <strong>Total Delivery Cost:</strong> formatCurrency(calculatedCosts[0]?.shipment_breakdown?.total_delivery_cost || 0)
                                                </div>
                                                <div className="col-md-6">
                                                    <strong>Total Customs Cost:</strong> formatCurrency(calculatedCosts[0]?.shipment_breakdown?.total_customs_cost || 0)<br/>
                                                    <strong>Total Other Cost:</strong> formatCurrency(calculatedCosts[0]?.shipment_breakdown?.total_other_cost || 0)<br/>
                                                    <strong className="text-primary">Total Shipment Cost:</strong> formatCurrency(calculatedCosts[0]?.shipment_breakdown?.total_shipment_cost || 0)
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>

                                    {/* Items Cost Breakdown */}
                                    <Table striped bordered hover responsive>
                                        <thead>
                                            <tr>
                                                <th>Item</th>
                                                <th>Qty</th>
                                                <th>Confirmed Price</th>
                                                <th>Landed Cost</th>
                                                <th>Suggested Resale</th>
                                                <th>Profit Margin</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {calculatedCosts.map(cost => (
                                                <tr key={cost.confirmed_line_id}>
                                                    <td>
                                                        <div className="fw-bold">{cost.stock_item_description}</div>
                                                        {cost.vendor_item_mapping?.vendor_description && (
                                                            <small className="text-muted">
                                                                Vendor: {cost.vendor_item_mapping.vendor_description}
                                                            </small>
                                                        )}
                                                    </td>
                                                    <td>{formatQuantity(cost.quantity)}</td>
                                                    <td>formatCurrency(cost.confirmed_price || cost.po_price || 0)</td>
                                                    <td className="fw-bold text-primary">formatCurrency(cost.suggested_cost || 0)</td>
                                                    <td className="fw-bold text-success">formatCurrency(cost.suggested_resale_price || 0)</td>
                                                    <td>{cost.cost_breakdown?.markup_percentage?.toFixed(1) || '0.0'}%</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </div>
                            </Tab>

                            {/* Detailed Breakdown Tab */}
                            <Tab eventKey="details" title="Detailed Breakdown">
                                <div className="mt-3">
                                    <p className="mb-3">
                                        <strong>Cost Allocation Method:</strong> Proportional allocation based on item values.
                                        Higher-value items absorb more of the shared shipping and customs costs.
                                    </p>
                                    
                                    {calculatedCosts.map(cost => (
                                        <Card key={cost.confirmed_line_id} className="mb-3">
                                            <Card.Header>
                                                <h6 className="mb-0">{cost.stock_item_description}</h6>
                                                {cost.vendor_item_mapping?.vendor_description && (
                                                    <small className="text-muted">
                                                        Vendor Item: {cost.vendor_item_mapping.vendor_description}
                                                        {cost.vendor_item_mapping.vendor_item_code && ` (${cost.vendor_item_mapping.vendor_item_code})`}
                                                    </small>
                                                )}
                                            </Card.Header>
                                            <Card.Body>
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <h6>Cost Components (per unit):</h6>
                                                        <Table size="sm">
                                                            <tbody>
                                                                <tr>
                                                                    <td>Confirmed Price:</td>
                                                                    <td className="text-end">formatCurrency(cost.cost_breakdown?.confirmed_price || 0)</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Freight Allocation:</td>
                                                                    <td className="text-end">formatCurrency(cost.cost_breakdown?.freight_cost_per_unit || 0)</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Delivery Allocation:</td>
                                                                    <td className="text-end">formatCurrency(cost.cost_breakdown?.delivery_cost_per_unit || 0)</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Customs Allocation:</td>
                                                                    <td className="text-end">formatCurrency(cost.cost_breakdown?.customs_cost_per_unit || 0)</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Other Costs:</td>
                                                                    <td className="text-end">formatCurrency(cost.cost_breakdown?.other_cost_per_unit || 0)</td>
                                                                </tr>
                                                                <tr className="fw-bold border-top">
                                                                    <td>True Landed Cost:</td>
                                                                    <td className="text-end text-primary">formatCurrency(cost.cost_breakdown?.landed_cost_per_unit || 0)</td>
                                                                </tr>
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <h6>Pricing Information:</h6>
                                                        <Table size="sm">
                                                            <tbody>
                                                                <tr>
                                                                    <td>Landed Cost:</td>
                                                                    <td className="text-end">{formatCurrency(cost.cost_breakdown?.landed_cost_per_unit || 0)}</td>
                                                                </tr>
                                                                <tr>
                                                                    <td>Markup ({cost.cost_breakdown?.markup_percentage?.toFixed(1) || '0'}%):</td>
                                                                    <td className="text-end">{formatCurrency(cost.cost_breakdown?.markup_amount || 0)}</td>
                                                                </tr>
                                                                <tr className="fw-bold border-top">
                                                                    <td>Suggested Resale Price:</td>
                                                                    <td className="text-end text-success">{formatCurrency(cost.cost_breakdown?.suggested_resale_price || 0)}</td>
                                                                </tr>
                                                                <tr className="text-muted">
                                                                    <td>Value Share of Shipment:</td>
                                                                    <td className="text-end">{cost.cost_breakdown?.value_percentage?.toFixed(1) || '0'}%</td>
                                                                </tr>
                                                            </tbody>
                                                        </Table>
                                                    </div>
                                                </div>
                                            </Card.Body>
                                        </Card>
                                    ))}
                                </div>
                            </Tab>

                            {/* AI Analysis Tab */}
                            <Tab eventKey="ai-analysis" title="AI Analysis">
                                <div className="mt-3">
                                    <Alert variant="info">
                                        <h6>AI Document Analysis Results</h6>
                                        <p>
                                            The AI system analyzed all vendor confirmation documents to extract:
                                        </p>
                                        <ul>
                                            <li><strong>Confirmed prices and quantities</strong> for each item</li>
                                            <li><strong>Shipping costs</strong> including freight, delivery, and customs</li>
                                            <li><strong>Item mappings</strong> between vendor descriptions and your stock items</li>
                                        </ul>
                                    </Alert>

                                    {calculatedCosts.map(cost => (
                                        cost.vendor_item_mapping && Object.keys(cost.vendor_item_mapping).length > 0 && (
                                            <Card key={cost.confirmed_line_id} className="mb-3">
                                                <Card.Header>
                                                    <h6 className="mb-0">Item Mapping: {cost.stock_item_description}</h6>
                                                </Card.Header>
                                                <Card.Body>
                                                    <div className="row">
                                                        <div className="col-md-6">
                                                            <strong>Our Item:</strong><br/>
                                                            {cost.stock_item_description}
                                                        </div>
                                                        <div className="col-md-6">
                                                            <strong>Vendor Item:</strong><br/>
                                                            {cost.vendor_item_mapping.vendor_description || 'Not mapped'}
                                                            {cost.vendor_item_mapping.vendor_item_code && (
                                                                <><br/><small>Code: {cost.vendor_item_mapping.vendor_item_code}</small></>
                                                            )}
                                                        </div>
                                                    </div>
                                                    {cost.vendor_item_mapping.matching_reason && (
                                                        <div className="mt-2">
                                                            <small className="text-muted">
                                                                <strong>Matching Reason:</strong> {cost.vendor_item_mapping.matching_reason}
                                                            </small>
                                                        </div>
                                                    )}
                                                    {cost.vendor_item_mapping.confidence && (
                                                        <div className="mt-1">
                                                            <small className="text-muted">
                                                                <strong>Confidence:</strong> {(cost.vendor_item_mapping.confidence * 100).toFixed(0)}%
                                                            </small>
                                                        </div>
                                                    )}
                                                </Card.Body>
                                            </Card>
                                        )
                                    ))}
                                </div>
                            </Tab>
                        </Tabs>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowCostModal(false)}>
                        Cancel
                    </Button>
                    <Button variant="primary" onClick={handleApplyCosts}>
                        Apply Suggested Landed Costs
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Centered Progress Indicator for AI Analysis */}
            {isCalculating && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%',
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 9999
                }}>
                    <div style={{
                        backgroundColor: 'white',
                        padding: '2rem',
                        borderRadius: '8px',
                        textAlign: 'center',
                        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
                    }}>
                        <Spinner animation="border" variant="primary" className="mb-3" />
                        <div>
                            <strong>AI Analysis in Progress...</strong>
                        </div>
                        <div className="text-muted mt-2">
                            Analyzing documents and calculating costs
                        </div>
                    </div>
                </div>
            )}
        </Container>
    );
}

export default BatchReceiveScreen;